<section class="max-w-6xl mx-auto space-y-6 pb-20">
  <div class="flex items-center justify-between">
    <div>
      <h1 class="text-3xl font-black tracking-tight">
        {{ isEditMode ? 'Edit Store' : 'Create New Store' }}
      </h1>
      <p class="text-base-content/60 font-medium">
        Configure location, logistics, and business-level overrides.
      </p>
    </div>
    <div class="flex gap-3">
      <button class="btn btn-ghost" (click)="cancel()">Cancel</button>
      <button
        class="btn btn-primary px-8"
        [disabled]="isSubmitting"
        (click)="submit()"
      >
        @if (isSubmitting) {
        <span class="loading loading-spinner loading-xs"></span>
        }
        {{ isEditMode ? 'Save Changes' : 'Launch Store' }}
      </button>
    </div>
  </div>

  <div class="grid grid-cols-1 lg:grid-cols-12 gap-6">
    <!-- Left Column: Form -->
    <div class="lg:col-span-4 space-y-6">
      <form [formGroup]="storeForm" class="space-y-4">
        <div class="card border border-base-300 bg-base-100 shadow-sm">
          <div class="card-body gap-4">
            <h3
              class="text-sm font-bold uppercase tracking-widest text-primary"
            >
              Identity
            </h3>

            <div class="form-control">
              <label class="label" for="store-name-input"
                ><span class="label-text font-semibold"
                  >Store Public Name</span
                ></label
              >
              <input
                type="text"
                id="store-name-input"
                formControlName="name"
                placeholder="e.g. Supidoo Fresh Indiranagar"
                class="input input-bordered input-md w-full"
              />
            </div>

            <div class="form-control">
              <label class="label" for="store-code-input">
                <span class="label-text font-semibold flex items-center gap-2">
                  Internal Store Code
                  <span
                    class="badge badge-xs badge-ghost opacity-50 uppercase tracking-tighter"
                    >Auto</span
                  >
                </span>
              </label>
              <input
                type="text"
                id="store-code-input"
                formControlName="store_code"
                placeholder="Will be generated..."
                class="input input-bordered input-md w-full uppercase font-mono bg-base-200"
              />
            </div>

            <div class="rounded-xl border border-primary/20 bg-primary/5 p-4">
              <p class="font-semibold">Custom delivery area</p>
              <p class="mt-1 text-sm text-base-content/60">
                Draw the exact service boundary on the map. At least three
                points are required.
              </p>
              <div class="mt-3 flex items-center gap-2">
                <span
                  class="badge"
                  [class.badge-success]="deliveryArea().length >= 3"
                  [class.badge-warning]="deliveryArea().length < 3"
                >
                  {{ deliveryArea().length }} boundary points
                </span>
                @if (isEditMode && deliveryArea().length < 3 &&
                f.store_radius.value > 0) {
                <span class="badge badge-ghost">Legacy radius active</span>
                }
              </div>
            </div>
          </div>
        </div>

        <div class="card border border-base-300 bg-base-100 shadow-sm">
          <div class="card-body gap-4">
            <h3 class="text-sm font-bold uppercase tracking-widest text-info">
              Legal & Tax Details
            </h3>

            <div class="form-control">
              <label class="label" for="store-legal-name-input">
                <span class="label-text font-semibold">Legal Business Name</span>
              </label>
              <input
                type="text"
                id="store-legal-name-input"
                formControlName="legal_business_name"
                placeholder="Registered business name"
                class="input input-bordered input-md w-full"
              />
            </div>

            <div class="form-control">
              <label class="label" for="store-gst-input">
                <span class="label-text font-semibold">GST Number</span>
              </label>
              <input
                type="text"
                id="store-gst-input"
                formControlName="gst_number"
                placeholder="29ABCDE1234F1Z5"
                autocomplete="off"
                maxlength="15"
                class="input input-bordered input-md w-full uppercase font-mono"
                [class.input-error]="f.gst_number.touched && f.gst_number.invalid"
              />
              @if (f.gst_number.touched && f.gst_number.hasError('pattern')) {
              <label class="label" for="store-gst-input">
                <span class="label-text-alt text-error">
                  Enter a valid 15-character GSTIN.
                </span>
              </label>
              }
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div class="form-control">
                <label class="label" for="store-pan-input">
                  <span class="label-text font-semibold">PAN Number</span>
                </label>
                <input
                  type="text"
                  id="store-pan-input"
                  formControlName="pan_number"
                  placeholder="ABCDE1234F"
                  autocomplete="off"
                  maxlength="10"
                  class="input input-bordered input-md w-full uppercase font-mono"
                  [class.input-error]="f.pan_number.touched && f.pan_number.invalid"
                />
                @if (f.pan_number.touched && f.pan_number.hasError('pattern')) {
                <label class="label" for="store-pan-input">
                  <span class="label-text-alt text-error">
                    Enter a valid 10-character PAN.
                  </span>
                </label>
                }
              </div>

              <div class="form-control">
                <label class="label" for="store-fssai-input">
                  <span class="label-text font-semibold">FSSAI License</span>
                </label>
                <input
                  type="text"
                  id="store-fssai-input"
                  formControlName="fssai_license_number"
                  placeholder="14 digit license"
                  autocomplete="off"
                  maxlength="14"
                  inputmode="numeric"
                  class="input input-bordered input-md w-full font-mono"
                  [class.input-error]="
                    f.fssai_license_number.touched &&
                    f.fssai_license_number.invalid
                  "
                />
                @if (f.fssai_license_number.touched &&
                f.fssai_license_number.hasError('pattern')) {
                <label class="label" for="store-fssai-input">
                  <span class="label-text-alt text-error">
                    Enter a valid 14-digit FSSAI license number.
                  </span>
                </label>
                }
              </div>
            </div>
          </div>
        </div>

        <div class="card border border-base-300 bg-base-100 shadow-sm">
          <div class="card-body gap-4">
            <h3
              class="text-sm font-bold uppercase tracking-widest text-secondary"
            >
              Address Context
            </h3>
            <div class="form-control">
              <label class="label" for="store-city-input"
                ><span class="label-text font-semibold">City</span></label
              >
              <input
                type="text"
                id="store-city-input"
                formControlName="city"
                class="input input-bordered input-md w-full"
              />
            </div>
            <div class="form-control">
              <label class="label" for="store-address-input"
                ><span class="label-text font-semibold"
                  >Detailed Street Address</span
                ></label
              >
              <textarea
                id="store-address-input"
                formControlName="address"
                class="textarea textarea-bordered h-24"
                placeholder="Full physical address..."
              ></textarea>
            </div>
          </div>
        </div>

        <!-- New: Contact & Logistics -->
        <div class="card border border-base-300 bg-base-100 shadow-sm">
          <div class="card-body gap-4">
            <h3 class="text-sm font-bold uppercase tracking-widest text-accent">
              Contact & Logistics
            </h3>
            <div class="grid grid-cols-1 gap-4">
              <div class="form-control">
                <label class="label" for="store-phone-input"
                  ><span class="label-text font-semibold"
                    >Phone Number</span
                  ></label
                >
                <input
                  type="tel"
                  id="store-phone-input"
                  formControlName="contact_number"
                  placeholder="+91..."
                  class="input input-bordered input-md w-full"
                />
              </div>
              <div class="form-control">
                <label class="label" for="store-email-input"
                  ><span class="label-text font-semibold"
                    >Support Email</span
                  ></label
                >
                <input
                  type="email"
                  id="store-email-input"
                  formControlName="email"
                  placeholder="store@example.com"
                  class="input input-bordered input-md w-full"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- New: Operating Hours -->
        <div class="card border border-base-300 bg-base-100 shadow-sm">
          <div class="card-body gap-4">
            <h3
              class="text-sm font-bold uppercase tracking-widest text-warning"
            >
              Operating Hours
            </h3>
            <div class="grid grid-cols-2 gap-4">
              <div class="form-control">
                <label class="label" for="opening-time-input"
                  ><span class="label-text font-semibold"
                    >Open Time</span
                  ></label
                >
                <input
                  type="time"
                  id="opening-time-input"
                  formControlName="opening_time"
                  class="input input-bordered input-md w-full"
                />
              </div>
              <div class="form-control">
                <label class="label" for="closing-time-input"
                  ><span class="label-text font-semibold"
                    >Close Time</span
                  ></label
                >
                <input
                  type="time"
                  id="closing-time-input"
                  formControlName="closing_time"
                  class="input input-bordered input-md w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>

    <!-- Right Column: Map -->
    <div class="lg:col-span-8 flex flex-col gap-6">
      <div
        class="card border border-base-300 bg-base-100 shadow-sm overflow-hidden h-full min-h-[500px]"
      >
        <div
          class="bg-base-200/50 p-4 border-b border-base-300 flex items-center justify-between gap-4"
        >
          <div class="flex-1 max-w-lg">
            <div class="join w-full shadow-sm">
              <input
                #locationSearch
                type="text"
                placeholder="Search locality or landmark..."
                class="input input-sm input-bordered join-item w-full"
                (focus)="enablePlacesSearch()"
              />
              <button class="btn btn-sm btn-primary join-item">Search</button>
            </div>
          </div>
          <div class="flex flex-wrap items-center justify-end gap-2">
            @if (!isDrawingDeliveryArea()) {
            <button
              class="btn btn-sm btn-primary"
              type="button"
              (click)="startDeliveryAreaDrawing()"
            >
              <i class="fa-solid fa-draw-polygon"></i>
              {{ deliveryArea().length ? 'Continue drawing' : 'Draw area' }}
            </button>
            } @else {
            <button
              class="btn btn-sm btn-success"
              type="button"
              [disabled]="deliveryArea().length < 3"
              (click)="finishDeliveryAreaDrawing()"
            >
              Finish area
            </button>
            }
            <button
              class="btn btn-sm btn-ghost"
              type="button"
              [disabled]="deliveryArea().length === 0"
              (click)="undoDeliveryAreaPoint()"
            >
              Undo point
            </button>
            <button
              class="btn btn-sm btn-ghost text-error"
              type="button"
              [disabled]="deliveryArea().length === 0"
              (click)="clearDeliveryArea()"
            >
              Clear
            </button>
          </div>
          <div class="flex items-center gap-4 text-xs font-mono">
            <div class="flex flex-col">
              <span class="opacity-40">LAT</span>
              <span class="font-bold">{{
                f.latitude.value | number : '1.4-4'
              }}</span>
            </div>
            <div class="flex flex-col">
              <span class="opacity-40">LNG</span>
              <span class="font-bold">{{
                f.longitude.value | number : '1.4-4'
              }}</span>
            </div>
          </div>
        </div>

        <div class="flex-1 relative">
          @if (mapsReady()) {
          <google-map
            height="100%"
            width="100%"
            [center]="center()"
            [zoom]="15"
            (mapClick)="onMapClick($event)"
          >
            @if (markerPosition()) {
            <map-marker
              [position]="markerPosition()!"
              [options]="{ draggable: true }"
              (markerDragEnd)="onMarkerDragEnd($event)"
            ></map-marker>
            }
            @if (deliveryArea().length >= 3) {
            <map-polygon
              [paths]="deliveryArea()"
              [options]="{
                fillColor: '#248842',
                fillOpacity: 0.2,
                strokeColor: '#248842',
                strokeWeight: 2,
                clickable: false
              }"
            >
            </map-polygon>
            }
            @if (isDrawingDeliveryArea() && deliveryArea().length >= 2) {
            <map-polyline
              [path]="deliveryArea()"
              [options]="{
                strokeColor: '#248842',
                strokeOpacity: 1,
                strokeWeight: 3,
                clickable: false
              }"
            ></map-polyline>
            }
            @if (isDrawingDeliveryArea()) {
              @for (point of deliveryArea(); track $index) {
              <map-marker
                [position]="point"
                [label]="($index + 1).toString()"
                [options]="{ clickable: false, opacity: 0.85 }"
              ></map-marker>
              }
            }
          </google-map>
          } @else {
          <div
            class="absolute inset-0 bg-base-200 flex flex-col items-center justify-center gap-3"
          >
            <span
              class="loading loading-spinner loading-lg text-primary"
            ></span>
            <p class="font-bold animate-pulse">
              Initializing Geospatial Engine...
            </p>
          </div>
          }
        </div>
        <div class="border-t border-base-300 bg-base-100 px-4 py-3 text-sm">
          @if (isDrawingDeliveryArea()) {
          <span class="font-semibold text-primary">
            Drawing mode: click around the map to add boundary points in order.
          </span>
          } @else {
          <span class="text-base-content/60">
            Map clicks move the store pin. Choose "Draw area" to edit the
            delivery boundary.
          </span>
          }
        </div>
      </div>
    </div>
  </div>

</section>
